import _ from 'lodash';
import moment, { Moment } from 'moment';
import { FC, memo } from 'react';
import { Flexbox, FlexItem, Icon, Text, useResize } from '@morf/ui';
import { LoaderOverlay } from '../LoaderOverlay';
import { SchedulingDate } from './SchedulingDate';
import { SchedulingDatePickerProps } from './types';
import { StyledSchedulingWeek } from './SchedulingDatePicker.css';
import { formatValueDateToMonth } from '../../../components/helpers/formatValueDateToMonth';
import { generateDateRange } from './generateDateRange';
import { EnrichedDate, generateEnrichedDates } from './generateEnrichedDates';
import { getHighlightedDates } from '../../helpers/getHighlightedDates';
import { goToNextMonth } from './goToNextMonth';
import { goToPrevMonth } from './goToPrevMonth';
import { isValueDateInCurrentMonth } from './isValueDateInCurrentMonth';
import { useEarliestHighlightedDate } from '../../../components/helpers/useEarliestHighlightedDate';
import { useSchedule } from '../../../components/context/schedule/useSchedule';
import { useTheme } from '@morf/theming';
import { values } from '@morf/proto/values/v1/values_ts_proto';

const SchedulingDatePicker: FC<SchedulingDatePickerProps> = ({
  monthlyAvailabilitySlots,
  closeSchedulingDatePicker,
  isLoading,
  multiSelect = false,
}) => {
  const theme = useTheme();
  const {
    endDate,
    hasAvailabilityEverBeenLoaded,
    isAutoSelectedDate,
    selectedDate,
    setHasAvailabilityEverBeenLoaded,
    setIsAutoSelectedDate,
    setSelectedDate,
    setStartDate,
    startDate,
  } = useSchedule();

  const now: Moment = moment();
  const dates: values.v1.Date[] = generateDateRange(startDate, endDate);

  const beginningOfMonth: values.v1.Date | undefined = dates.find(
    (d) => d.day === 1
  );

  if (!beginningOfMonth) {
    return null;
  }

  const isInCurrentMonth: boolean = isValueDateInCurrentMonth(
    beginningOfMonth,
    now
  );

  const highlightedDates: values.v1.Date[] = getHighlightedDates(
    monthlyAvailabilitySlots
  );

  const enrichedDates: EnrichedDate[] = generateEnrichedDates(
    dates,
    highlightedDates,
    selectedDate,
    beginningOfMonth
  );

  const dayAbbreviations: string[] = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const columnSize: string = 'calc(100%/7)';
  const selectedWeek: values.v1.Date[] = [];

  const handleMonthChange = (date: values.v1.Date) => {
    setIsAutoSelectedDate(true);
    setSelectedDate(undefined);
    setStartDate(date);
  };

  const handlePrevMonthClick = () => {
    const prevMonth = goToPrevMonth(beginningOfMonth);
    handleMonthChange(prevMonth);
  };

  const handleNextMonthClick = () => {
    const nextMonth = goToNextMonth(beginningOfMonth);
    handleMonthChange(nextMonth);
  };

  const handleDateSelection = (date: values.v1.Date) => {
    setIsAutoSelectedDate(false);
    setSelectedDate(date);
    if (closeSchedulingDatePicker) closeSchedulingDatePicker();
  };

  const availableDates = enrichedDates.reduce<values.v1.Date[]>((acc, date) => {
    if (date.isCurrentMonth && date.isAvailable) {
      acc.push(date.date);
    }
    return acc;
  }, []);

  useEarliestHighlightedDate({
    availableDates,
    beginningOfMonth,
    handleNextMonthClick,
    hasAvailabilityEverBeenLoaded,
    isAutoSelectedDate,
    isLoading,
    now,
    nrOfMonthsToAutoAdvance: theme.nrOfMonthsToAutoAdvance || 1,
    selectedDate,
    setHasAvailabilityEverBeenLoaded,
    setSelectedDate,
  });

  const responsiveSchedulingDatePicker = useResize({
    p: {
      xxl: 1.25,
      xl: 1.25,
      lg: 1.25,
      md: 0.75,
      sm: 0.75,
      xs: 0.5,
    },
  });

  return (
    <Flexbox
      data-testid='scheduling-date-picker'
      backgroundColor='transparent'
      direction='column'
      alignItems='center'
      height='100%'
      minWidth='16rem'
      gap={1}
      {...responsiveSchedulingDatePicker}
    >
      <Flexbox px={0.5}>
        <Icon
          name='chevron-left'
          cursor={isInCurrentMonth ? 'default' : 'pointer'}
          onClick={isInCurrentMonth ? undefined : handlePrevMonthClick}
          stroke={
            isInCurrentMonth
              ? theme.colors.text.muted
              : theme.colors.main.primary.darker
          }
          size={1.25}
        />
        <Text tag='h4' color={theme.colors.text.body}>
          {formatValueDateToMonth(beginningOfMonth)}
        </Text>
        <Icon
          name='chevron-right'
          cursor='pointer'
          onClick={handleNextMonthClick}
          stroke={theme.colors.main.primary.darker}
          size={1.25}
        />
      </Flexbox>
      <Flexbox>
        {dayAbbreviations.map((day, index) => (
          <FlexItem key={index} flexBasis={columnSize}>
            <Text tag='p2' align='center' color={theme.colors.text.muted}>
              {day}
            </Text>
          </FlexItem>
        ))}
      </Flexbox>

      <Flexbox
        flexWrap='wrap'
        justifyContent='flex-start'
        alignItems='center'
        position='relative'
        gap={0}
      >
        {isLoading && <LoaderOverlay />}

        {enrichedDates.map((day, index) => {
          if (day.isSelectedWeek) selectedWeek.push(day.date);
          const handleClick = () => handleDateSelection(day.date);

          return (
            <FlexItem key={index} flexBasis={columnSize} alignSelf='center'>
              {multiSelect ? (
                <StyledSchedulingWeek
                  isSelectedWeek={day.isSelectedWeek}
                  isLoading={isLoading}
                  isFirstDate={selectedWeek[0] === day.date}
                  isLastDate={selectedWeek[6] === day.date}
                >
                  <SchedulingDate
                    date={day.date}
                    isActive={day.isSelectedWeek && day.isAvailable}
                    isDisabled={!day.isAvailable}
                    isHidden={false}
                    isLoading={isLoading}
                    onClick={handleClick}
                  />
                </StyledSchedulingWeek>
              ) : (
                <SchedulingDate
                  date={day.date}
                  isActive={day.isSelectedDate}
                  isDisabled={!day.isAvailable || !day.isCurrentMonth}
                  isHidden={!day.isCurrentMonth}
                  isLoading={isLoading}
                  onClick={handleClick}
                />
              )}
            </FlexItem>
          );
        })}
      </Flexbox>
    </Flexbox>
  );
};

export const MemoizedSchedulingDatePicker = memo(SchedulingDatePicker);
